import {
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FlagIcon,
  MapPinIcon,
  RocketLaunchIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Heading, Button, Alert } from '@restworld/ui-ds-base';
import { EditButton } from './card-wrapper';
import { useMemo, useState } from 'react';
import { cn, toDateFormat, useModal } from '@restworld/utils-common';
import { ProfileEditModal } from './profile-edit';
import { WorkerPastExperience } from '@restworld/utility-types';
import { PresentationCard } from '@restworld/ui-components';
import { animated, useSpring } from '@react-spring/web';
import { useProfileCommonIntl } from './hooks';

export const WorkerExperienceItem = ({
  experienceIndex,
  workerPastExperience,
}: {
  experienceIndex: number;
  workerPastExperience: WorkerPastExperience;
}) => {
  const { missingInfo: errorMessage, translate: t, missingInfoText } = useProfileCommonIntl();
  const [expandMore, setExpandMore] = useState(false);
  const { show: showModal, open: openModal, close: closeModal } = useModal();
  const { sector, role, restaurantName, startDate, endDate, description, city, skills, isActive } = useMemo(() => {
    const sector = workerPastExperience?.domain_interest_var?.label;
    const role = workerPastExperience?.occupation?.label_it?.label;
    const restaurantName = workerPastExperience?.restaurant_name;
    const startDate = workerPastExperience?.from_date
      ? toDateFormat(workerPastExperience?.from_date, 'dd/MM/yyyy')
      : undefined;
    const endDate = workerPastExperience?.to_date
      ? toDateFormat(workerPastExperience?.to_date, 'dd/MM/yyyy')
      : undefined;
    const description = workerPastExperience?.description;
    const city = workerPastExperience?.city;
    const skills = (workerPastExperience?.skills?.map((skill) => skill?.label_it?.label) || []).filter(
      (skill) => skill
    );
    const isActive = workerPastExperience?.currently_working;
    return {
      sector,
      role,
      restaurantName,
      startDate,
      endDate,
      description,
      city,
      skills,
      isActive,
    };
  }, [
    workerPastExperience?.domain_interest_var?.label,
    workerPastExperience?.occupation?.label_it?.label,
    workerPastExperience?.restaurant_name,
    workerPastExperience?.from_date,
    workerPastExperience?.to_date,
    workerPastExperience?.description,
    workerPastExperience?.city,
    workerPastExperience?.skills,
    workerPastExperience?.currently_working,
  ]);
  const isInformationMissing =
    !sector ||
    !role ||
    !restaurantName ||
    !startDate ||
    !city ||
    !description ||
    skills.length === 0 ||
    (!endDate && !isActive);

  const viewContentStyles = useSpring({
    opacity: !showModal ? 1 : 0,
    transform: !showModal ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const editContentStyles = useSpring({
    opacity: showModal ? 1 : 0,
    transform: showModal ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const expandMoreStyles = useSpring({
    opacity: expandMore ? 1 : 0,
    transform: expandMore ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const expandLessStyles = useSpring({
    opacity: !expandMore ? 1 : 0,
    transform: !expandMore ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const roleEl = (
    <PresentationCard
      label={t('common:role')}
      header={role || errorMessage}
      isError={!role}
      icon={<UserIcon className="w-6 h-6" />}
    />
  );

  const restaurantNameEl = (
    <PresentationCard
      label={t('common:local')}
      header={restaurantName || errorMessage}
      icon={<BuildingOfficeIcon className="w-6 h-6" />}
      isError={!restaurantName}
    />
  );

  const startDateEl = (
    <PresentationCard
      label={t('common:start_date')}
      header={startDate || errorMessage}
      isError={!startDate}
      icon={<RocketLaunchIcon className="w-6 h-6" />}
    />
  );

  const endDateEl = (
    <PresentationCard
      label={t('common:end_date')}
      header={endDate || (!isActive ? errorMessage : t('common:present'))}
      icon={<FlagIcon className="w-6 h-6" />}
      isError={!endDate && !isActive}
    />
  );

  return (
    <div className="flex flex-col w-full overflow-hidden">
      {!showModal && (
        <div className="flex flex-row justify-between  items-center w-full mb-4">
          <Heading size={5} weight="medium">
            {t('common:experience')} {experienceIndex + 1}
          </Heading>
          <EditButton onClick={() => openModal()} />
        </div>
      )}
      {showModal && (
        <animated.div style={editContentStyles} className="w-full">
          <ProfileEditModal
            showModal={showModal}
            onClose={closeModal}
            editPageType="work_experience"
            mode="edit"
            editPageTitle={`${t('common:experience')} ${experienceIndex + 1}`}
            key={`esperienza-${experienceIndex + 1}`}
            editIndex={experienceIndex}
          />
        </animated.div>
      )}
      {!showModal && (
        <animated.div style={viewContentStyles} className="w-full">
          {isInformationMissing && <Alert text={missingInfoText} className="mb-4" />}
          {!expandMore && (
            <CardsContainer style={expandLessStyles}>
              <CardsCol>
                {roleEl}
                {restaurantNameEl}
              </CardsCol>
              <CardsCol>
                {startDateEl}
                {endDateEl}
              </CardsCol>
            </CardsContainer>
          )}
          {expandMore && (
            <CardsContainer style={expandMoreStyles}>
              <CardsCol>
                <PresentationCard
                  label={t('common:sector')}
                  header={sector || errorMessage}
                  icon={<BriefcaseIcon className="w-6 h-6" />}
                  isError={!sector}
                />
                {roleEl}
                {restaurantNameEl}
              </CardsCol>
              <CardsCol>
                {startDateEl}
                {endDateEl}
                <PresentationCard
                  label={t('common:work_location')}
                  header={city || errorMessage}
                  hide={!expandMore}
                  icon={<MapPinIcon className="w-6 h-6" />}
                  isError={!city}
                />
              </CardsCol>
            </CardsContainer>
          )}
          {expandMore && (
            <animated.div style={expandMoreStyles} className="flex flex-col w-full items-start mt-4 gap-4">
              <PresentationCard
                label={t('common:job_description')}
                header={description || errorMessage}
                icon={<BookOpenIcon className="w-6 h-6" />}
                isError={!description}
              />
            </animated.div>
          )}
          {expandMore && (
            <animated.div style={expandMoreStyles} className="flex flex-col gap-4 items-start w-full mt-6">
              <Heading size={5} weight="medium">
                {t('common:skills_learned')}
              </Heading>
              <div className="grid lg:grid-cols-2 grid-cols-1 gap-x-9 gap-y-4 w-full">
                {skills.map((skill, index) => (
                  <div key={index} className="flex flex-row gap-2 items-center justify-start">
                    <CheckCircleIcon className="w-6 h-6 min-w-[24px] text-primary-700" />
                    <Heading size={5} weight="normal" color="muted-foreground">
                      {skill}
                    </Heading>
                  </div>
                ))}
                {skills.length === 0 && errorMessage}
              </div>
            </animated.div>
          )}
          <div className="flex flex-row w-full justify-center items-start mt-6">
            <Button
              EndIcon={!expandMore ? ChevronDownIcon : ChevronUpIcon}
              variant="tertiary"
              className={'focus:ring-0'}
              onClick={() => setExpandMore((prev) => !prev)}
            >
              {!expandMore ? t('common:show_more_information') : t('common:show_less_information')}
            </Button>
          </div>
        </animated.div>
      )}
    </div>
  );
};

export const CardsContainer = ({
  className,
  style,
  children,
  ...props
}: { className?: string; style?: any; children?: React.ReactNode } & Record<string, any>) => (
  <animated.div className={cn('flex lg:flex-row flex-col lg:gap-8 gap-4 w-full', className)} style={style} {...props}>
    {children}
  </animated.div>
);

export const CardsCol = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col lg:w-1/2 w-full items-start justify-start gap-4', className)} {...props} />
);
